// Service worker update lifecycle, kept in one place so the entry stays lean and
// the refresh prompt can activate a waiting worker without threading the
// registration through React.

let updateReadyRegistration = null;
let reloading = false;

// A new worker has installed while an old one still controls the page, so an
// update is ready to apply on the user's next refresh.
const markUpdateReady = (registration, onUpdateReady) => {
  updateReadyRegistration = registration;
  if (onUpdateReady) {
    onUpdateReady(registration);
  }
};

const trackInstalling = (worker, registration, onUpdateReady) => {
  if (!worker) return;
  worker.addEventListener('statechange', () => {
    // `controller` is only set once a worker already controls the page, so its
    // presence distinguishes an update from the very first install.
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      markUpdateReady(registration, onUpdateReady);
    }
  });
};

// Registers the service worker and reports when a new build is waiting to take
// over. `onUpdateReady(registration)` fires once an updated worker is installed.
export const registerServiceWorker = (onUpdateReady) => {
  // Reload once the new worker takes control (triggered by skipWaitingAndReload).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  return navigator.serviceWorker.register('/sw.js').then(
    (registration) => {
      // An update may already be parked from a previous visit.
      if (!registration) return null;
      if (registration.waiting && navigator.serviceWorker.controller) {
        markUpdateReady(registration, onUpdateReady);
      }

      registration.addEventListener('updatefound', () => {
        trackInstalling(registration.installing, registration, onUpdateReady);
      });

      return registration;
    },
    (err) => {
      console.log('ServiceWorker registration failed: ', err);
      return null;
    }
  );
};

// Tells the waiting worker to activate; the controllerchange handler then reloads
// onto the fresh assets. Falls back to a plain reload when there is no worker to
// coordinate with (e.g. Safari, which does not keep a service worker).
export const skipWaitingAndReload = (registration) => {
  const target = registration || updateReadyRegistration;
  if (target && target.waiting) {
    target.waiting.postMessage({ type: 'SKIP_WAITING' });
    return;
  }
  window.location.reload();
};
